'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@playbooks/ui/components/dropdown-menu'
import { Button } from '@playbooks/ui/components/button'
import { ChevronDownIcon } from 'lucide-react'

import type { WebPlayLocalizationService } from '../types'

interface LanguageSwitcherProps {
  service: WebPlayLocalizationService
}

function getLanguageName(language: string) {
  try {
    const name = new Intl.DisplayNames([language], { type: 'language' }).of(language)

    if (name) return name.charAt(0).toLocaleUpperCase(language) + name.slice(1)
  } catch {
    // Fall back to the language code when the browser does not recognize a locale.
  }

  return language.toUpperCase()
}

export default function LanguageSwitcher({ service }: LanguageSwitcherProps) {
  const { t } = useTranslation()
  const [languages, setLanguages] = useState<string[]>([])
  const [currentLanguage, setCurrentLanguage] = useState('')

  useEffect(() => {
    // Embedded plays use the parent page's switcher; standalone plays can opt out too.
    if (window.self !== window.top) {
      return
    }

    const synchronize = () => {
      setLanguages(service.getAvailableLanguages())
      setCurrentLanguage(service.getCurrentLanguage())
    }

    synchronize()
    return service.subscribe(synchronize)
  }, [service])

  if (!languages.length || !currentLanguage) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="min-w-18"
          aria-label={t('footer.languageSwitcher', 'Select language')}
        >
          <span lang={currentLanguage} translate="no" data-wg-notranslate="">
            {getLanguageName(currentLanguage)}
          </span>
          <ChevronDownIcon data-icon="inline-end" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="center" translate="no" data-wg-notranslate="">
        <DropdownMenuGroup>
          <DropdownMenuRadioGroup value={currentLanguage} onValueChange={language => service.switchTo(language)}>
            {languages.map(language => (
              <DropdownMenuRadioItem key={language} value={language} lang={language}>
                {getLanguageName(language)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
